import { colors } from '@EVOCORP/corechat-theme';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { TemplateInterface, WppComponentsType } from 'store/reducers/templates';
import parseTextToHtml from 'utils/parseTextToHtml';
import { replaceTemplateVariables } from 'utils/replaceTemplateVariables';
import { MessageVariables } from 'utils/validators';

import TemplateHeader from './TemplateHeader';
import TemplateButtons from './TemplateMessageButtons';

const useStyles = makeStyles(theme => ({
  cardContainer: (props: { isSkeleton?: boolean }) => ({
    borderRadius: '6px',
    boxShadow: 'none',
    width: props.isSkeleton ? '100%' : '77%',
    position: props.isSkeleton ? 'relative' : 'absolute',
    top: props.isSkeleton ? 'unset' : '25%',
    left: props.isSkeleton ? 'unset' : '7.5%',
    backgroundColor: colors.light.background.paper,
    color: colors.light.text.primary,
  }),
  cardContent: {
    padding: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
  },
}));

interface TemplatePreviewCardProps {
  template?: TemplateInterface;
  templateComponents?: WppComponentsType[];
  messageVariables?: MessageVariables | null;
  readOnly?: boolean;
}

function findComponent(
  type: string,
  primary: WppComponentsType[],
  fallback: WppComponentsType[] = [],
): WppComponentsType {
  return (
    primary.find(c => c.type === type) ??
    fallback.find(c => c.type === type) ??
    ({} as WppComponentsType)
  );
}

function TemplatePreviewCard({
  messageVariables,
  template,
  templateComponents,
  readOnly = false,
}: TemplatePreviewCardProps) {
  const isSkeleton = Boolean(templateComponents);
  const classes = useStyles({ isSkeleton });

  const primaryComponents = template?.wppComponents ?? [];
  const fallbackComponents = templateComponents ?? [];

  const header = findComponent('HEADER', primaryComponents, fallbackComponents);
  const body = findComponent('BODY', primaryComponents, fallbackComponents);
  const footer = findComponent('FOOTER', primaryComponents, fallbackComponents);
  const buttons = findComponent(
    'BUTTONS',
    primaryComponents,
    fallbackComponents,
  );

  const processedBodyText = replaceTemplateVariables(
    body?.text || 'Mensagem',
    messageVariables?.body?.variables,
  );

  return (
    <Card className={classes.cardContainer}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.stack}>
          <TemplateHeader
            templateHeaderData={header}
            readOnly={readOnly}
            currentUrl={messageVariables?.header?.url}
            headerVariables={messageVariables?.headerVariables}
          />

          <Typography
            variant="body2"
            style={{ fontSize: isSkeleton ? 12 : 13 }}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(processedBodyText),
            }}
          />

          {footer?.text && (
            <Typography
              variant="caption"
              style={{ fontSize: isSkeleton ? 12 : 12 }}
              dangerouslySetInnerHTML={{
                __html: parseTextToHtml(footer.text),
              }}
            />
          )}

          <TemplateButtons buttons={buttons?.buttons ?? []} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default TemplatePreviewCard;
